

import React, { useState, useEffect, useMemo } from "react";
import axios from '../api/axiosConfig';
import {
    Box,
    Pagination,
    Skeleton,
    Button,
    Chip,
    Typography,
    Stack,
    Avatar,
} from "@mui/material";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, InputBase, Paper } from "@mui/material";
import RecordDetailModal from "../components/RecordDetailModal";
import { getInitials, stringToColor } from "../utils/DataHelper";
import { useTheme, useMediaQuery } from "@mui/material";

export default function BillingGenerator() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 100;

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleOpenDetail = (row) => {
        setSelectedRecord(row);
        setModalOpen(true);
    };



    // ------------------------
    // 🔥 API CALL HERE
    // ------------------------
    const fetchData = async (query = {}) => {
        try {
            setLoading(true);

            const params = new URLSearchParams(query).toString();
            const url = `/jemaat.php?isbetaniamember=1${params}`;

            const resp = await axios.get(url);
            setRows(resp.data); // API must return array of rows
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };




    // Load initial data
    useEffect(() => {
        fetchData();
    }, []);


    const handleGenerateBilling = async () => {
        if (!filteredRows || filteredRows.length === 0) {
            alert("No data available.");
            return;
        }

        const confirmGen = window.confirm(`Generate billing untuk ${filteredRows.length} jemaat?`);
        if (!confirmGen) return;

        setLoading(true);

        let success = 0;
        let failed = 0;

        // Loop per-row (serial, lebih aman, hindari spike API)
        for (const r of filteredRows) {
            try {
                // const resp= await axios.get(`/billinggenofmissing.php?membercode=${r.idjemaat}&amount=2500`);
                console.log("Generating billing for:", r.name_full, r.idjemaat);

                const resp = await axios.post(`/billinggen.php`,
                    {
                        membercode: r.idjemaat,
                        cardyear: 2026,
                        amount: 2500
                    }
                    , {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    });
                console.log("Billing created for:", r.idjemaat, resp.data);
                success++;
            } catch (err) {
                console.error("Billing failed for:", r.idjemaat, err);
                failed++;
            }
        }

        setLoading(false);

        alert(`Billing created.\nSuccess: ${success}\nFailed: ${failed}`);
    };




    // ------------------------
    // 🔍 Client-side filtering
    // ------------------------
    const filteredRows = useMemo(() => {
        if (!search) return rows;
        const term = search.toLowerCase();
        return rows.filter(
            (r) =>
                r.name_full?.toLowerCase().includes(term) ||
                r.idjemaat?.toLowerCase().includes(term) ||
                r.phone_mobile?.toLowerCase().includes(term)
        );
    }, [rows, search]);

    const exportToExcel = () => {
        const exportData = filteredRows.map(row => ({
            ID: row.idjemaat,
            FullName: row.name_full,
            Nick: row.name_nick,
            Phone: row.phone_mobile,
            Status: row.status_jemaat,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Pilgrims");

        XLSX.writeFile(wb, "pilgrims.xlsx");
    };

    // ------------------------
    // 📄 Pagination (100 rows)
    // ------------------------

    const paginatedRows = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page]);

    return (
        < >
            <Box sx={{
                height: isMobile ? "calc(100dvh - 80px)" : "calc(100dvh - 26px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        fontWeight: 700,
                        color: "#1f2937",
                        textShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        letterSpacing: "0.4px",
                    }}
                >
                    Proses Awal Tahun - Generate Billing
                </Typography>



                <Box
                    sx={{
                        mb: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    {/* Google Search Box */}
                    <Paper
                        elevation={0}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            maxWidth: 500,
                            p: "6px 12px",
                            borderRadius: "40px",
                            background: "#fff",
                            border: "1px solid #e0e0e0",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            transition: "0.3s ease",
                            "&:hover": {
                                boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                            },
                            "&:focus-within": {
                                borderColor: "#4285f4",
                                boxShadow: "0 0 0 3px rgba(66,133,244,0.25)",
                            },
                        }}
                    >
                        <SearchIcon sx={{ opacity: 0.6, mr: 1 }} />

                        <InputBase
                            placeholder="Cari Anggota Betania..."
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            sx={{
                                fontSize: "1rem",
                                ml: 1,
                            }}
                        />

                        {search.length > 0 && (
                            <IconButton
                                onClick={() => setSearch("")}
                                size="small"
                                sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Paper>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateBilling}
                        sx={{
                            ml: 2,
                            height: 50,
                            borderRadius: 3,
                            textTransform: "none",
                            px: 3,
                            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                        }}
                    >
                        Generate Billing
                    </Button>

                    {/* Export Button */}
                    <Button
                        variant="contained"
                        color="success"
                        onClick={exportToExcel}
                        sx={{
                            ml: 2,
                            height: 50,
                            borderRadius: 3,
                            textTransform: "none",
                            px: 3,
                            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                        }}
                        startIcon={<DownloadIcon />}
                    >
                        Download
                    </Button>
                </Box>



                {/* Main container */}
                {/* <Card sx={{ borderRadius: 0, overflow: "hidden", boxShadow: 1, mt: 2 }}>
                    <CardContent sx={{ p: 0,sx:{overflowX:'auto',background:'#DDDDDD'}    }}> */}

                <Box sx={{
                    flex: 1,               // 🔥 fills available height
                    overflowY: "auto",
                    mt: 1,
                    borderRadius: 1,
                    WebkitOverflowScrolling: "touch",
                }}>                   {/* Skeleton Loader */}
                    {loading ? (
                        [...Array(12)].map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={45}
                                sx={{ my: 0.5, borderRadius: 1 }}
                            />
                        ))
                    ) : (
                         <>


                            <Stack spacing={1.5}>
                                {paginatedRows.map((row) => {
                                    const avatarColor = stringToColor(row.name_full);

                                    return (
                                        <Box
                                            key={row.id}
                                            onClick={() => handleOpenDetail(row)}
                                            sx={{
                                                p: 2,
                                                bgcolor: "background.paper",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                                cursor: "pointer",
                                                transition: "all .2s",
                                                "&:hover": {
                                                    bgcolor: "action.hover",
                                                    transform: "translateY(-1px)",
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                                },
                                            }}
                                        >
                                            {/* Header */}
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                {/* Avatar */}
                                                <Avatar
                                                    sx={{
                                                        bgcolor: avatarColor,
                                                        color: "#fff",
                                                        fontWeight: 700,
                                                        width: 42,
                                                        height: 42,
                                                        fontSize: 15,
                                                    }}
                                                >
                                                    {getInitials(row.name_full)}
                                                </Avatar>

                                                <Box flex={1}>
                                                    <Typography fontSize={18} fontWeight={700}>
                                                        {row.name_full}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {row.alamat}
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label={row.idjemaat}
                                                    color={"primary"}
                                                    size="small"
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </Stack>


                                        </Box>
                                    );
                                })}
                            </Stack>



                        </>
                    )}

                </Box>
                {!loading && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            borderTop: "1px solid #e0e0e0",
                            background: "#f0f0f0",
                            px: 1,
                            // py: 0.5,
                            pb: "env(safe-area-inset-bottom)", // 👈 iOS fix
                        }} >
                        <Stack direction="row"
                            spacing={isMobile ? 0.5 : 2}
                            alignItems="center"
                            justifyContent="space-between"
                        >


                            <Pagination
                                count={Math.ceil(filteredRows.length / rowsPerPage)}
                                page={page}
                                      siblingCount={0} boundaryCount={1} 
                                onChange={(e, v) => setPage(v)}
                                shape="rounded"
                                sx={{
                                    boxShadow: "none",
                                    backgroundColor: "#f0f0f0",
                                    "& .MuiPaginationItem-root": {
                                        borderRadius: "12px",
                                        fontSize: "13px",
                                        minWidth: "36px",
                                        height: "36px",
                                        backgroundColor: "#e5e5e5",
                                    },
                                    "& .MuiPaginationItem-root:hover": {
                                        backgroundColor: "#d0d0d0",
                                    },
                                    "& .Mui-selected": {
                                        backgroundColor: "#343434 !important",
                                        color: "#fff",
                                    },
                                }}
                            />
                            <Box
                                sx={{
                                    padding: "10px 16px",
                                    background: "#fafafa",
                                    display: "inline-block",
                                    backgroundColor: "#f0f0f0",
                                    textAlign: "center", 
                                }}
                            >

                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#333" }}>
                                    {filteredRows.length.toLocaleString()}
                                </Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 500, color: "#333" }}>
                                    Record
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                )}
                {/* </CardContent>

                </Card> */}
            </Box>
            <RecordDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                data={selectedRecord}
            />
        </ >
    );
}



 